/** Светлая/тёмная тема: выбор пользователя или системная настройка. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "rus_ii_theme";

interface ThemeContextValue {
  /** Что выбрал пользователь, включая «как в системе». */
  choice: ThemeChoice;
  /** Тема, которая реально применена сейчас. */
  theme: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
  /** Переключение светлая ⇄ тёмная (выходит из режима «системная»). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  choice: "system",
  theme: "light",
  setChoice: () => {},
  toggle: () => {},
});

function readStoredChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    /* приватный режим или заблокированные куки — просто берём системную */
  }
  return "system";
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? "dark" : "light");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const theme: ResolvedTheme = choice === "system" ? system : choice;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.style.colorScheme = theme;
  }, [theme]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* не критично: тема просто не переживёт перезагрузку */
    }
  }, []);

  const toggle = useCallback(() => {
    setChoice(theme === "dark" ? "light" : "dark");
  }, [theme, setChoice]);

  const value = useMemo(
    () => ({ choice, theme, setChoice, toggle }),
    [choice, theme, setChoice, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
